/**
 * StudentDialog - Add/Edit student dialog
 *
 * Modal form for creating or editing a student.
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import type { LocalStudent } from "@/types/local-db.types";

// ============================================================================
// Schema
// ============================================================================

const studentSchema = z.object({
  name: z.string().min(1, "Student name is required").max(100, "Name too long"),
  sheetNumber: z
    .string()
    .optional()
    .refine(
      (val) => !val || (/^\d+$/.test(val) && parseInt(val) >= 1 && parseInt(val) <= 999),
      "Sheet number must be 1-999"
    ),
});

type StudentFormData = z.infer<typeof studentSchema>;

// ============================================================================
// Component
// ============================================================================

interface StudentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingStudent?: LocalStudent | null;
  nextSheetNumber?: string;
  onSubmit: (data: StudentFormData) => void;
}

export function StudentDialog({
  open,
  onOpenChange,
  editingStudent,
  nextSheetNumber,
  onSubmit,
}: StudentDialogProps) {
  const form = useForm<StudentFormData>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      name: "",
      sheetNumber: "",
    },
  });

  // Reset form when dialog opens with edit data
  useEffect(() => {
    if (open) {
      if (editingStudent) {
        form.reset({
          name: editingStudent.name,
          sheetNumber: editingStudent.sheetNumber,
        });
      } else {
        form.reset({
          name: "",
          sheetNumber: "",
        });
      }
    }
  }, [open, editingStudent, form]);

  const handleSubmit = (data: StudentFormData) => {
    onSubmit(data);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {editingStudent ? "Edit Student" : "Add Student"}
          </DialogTitle>
          <DialogDescription>
            {editingStudent
              ? "Update the student details."
              : "Add a new student to this class."}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Student Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Alice Johnson"
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sheetNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Sheet Number</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={nextSheetNumber ? `Next: ${nextSheetNumber}` : "Auto-assign"}
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {editingStudent
                      ? "Change the assigned sheet number."
                      : "Leave blank to auto-assign the next number."}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {editingStudent ? "Save Changes" : "Add Student"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
